import { ToolbarButton } from '@provision/rich-text-editor';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { EditorToolbarButton } from './editor-toolbar-button';
import { StyledDynamicIcon } from './styled-dynamic-icon';

type BubbleMenuToolbarButtonProps = {
  button: ToolbarButton;
  delay: number;
};

export const BubbleMenuToolbarButton = ({ button, delay }: BubbleMenuToolbarButtonProps) => {
  const { title, onClick, isActive, disabled, icon } = button;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const closeTooltip = useCallback(() => {
    clearHoverTimer();
    setIsTooltipOpen(false);
  }, [clearHoverTimer]);

  const scheduleTooltipOpen = useCallback(() => {
    clearHoverTimer();

    hoverTimerRef.current = setTimeout(() => {
      setIsTooltipOpen(true);
      hoverTimerRef.current = null;
    }, delay);
  }, [clearHoverTimer, delay]);

  useEffect(() => {
    return () => {
      clearHoverTimer();
    };
  }, [clearHoverTimer]);

  const handleClick = useCallback(() => {
    closeTooltip();
    onClick?.();
  }, [closeTooltip, onClick]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Escape') {
        closeTooltip();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        closeTooltip();
      }
    },
    [closeTooltip],
  );

  return (
    <Tooltip
      title={title}
      open={isTooltipOpen}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      PopperProps={{
        disablePortal: true,
      }}
    >
      <span
        onMouseEnter={scheduleTooltipOpen}
        onMouseLeave={closeTooltip}
        onMouseDown={closeTooltip}
        onFocus={scheduleTooltipOpen}
        onBlur={closeTooltip}
        onKeyDown={handleKeyDown}
        style={{ display: 'inline-flex' }}
      >
        <EditorToolbarButton onClick={handleClick} isActive={isActive} disabled={disabled}>
          <StyledDynamicIcon filled name={icon} />
        </EditorToolbarButton>
      </span>
    </Tooltip>
  );
};
